export const MIME_EXTENSIONS = {
  // Images
  'image/png': { extensions: ['png'], inlineText: false },
  'image/jpeg': { extensions: ['jpg', 'jpeg'], inlineText: false },
  'image/webp': { extensions: ['webp'], inlineText: false },
  'image/gif': { extensions: ['gif'], inlineText: false },

  // Documents
  'application/pdf': { extensions: ['pdf'], inlineText: false },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    extensions: ['docx'],
    inlineText: false,
  },
  'application/msword': { extensions: ['doc', 'dot'], inlineText: false },
  'application/vnd.oasis.opendocument.text': { extensions: ['odt'], inlineText: false },
  'application/rtf': { extensions: ['rtf'], inlineText: true },
  'application/vnd.apple.pages': { extensions: ['pages'], inlineText: false },

  // Spreadsheets
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    extensions: ['xlsx'],
    inlineText: false,
  },
  'application/vnd.ms-excel': { extensions: ['xls'], inlineText: false },
  'text/csv': { extensions: ['csv'], inlineText: true },
  'text/tab-separated-values': { extensions: ['tsv'], inlineText: true },
  'text/x-iif': { extensions: ['iif'], inlineText: true },

  // Presentations
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    extensions: ['pptx'],
    inlineText: false,
  },
  'application/vnd.ms-powerpoint': { extensions: ['ppt'], inlineText: false },
  'application/vnd.apple.keynote': { extensions: ['keynote'], inlineText: false },

  // Text & markup
  'text/plain': {
    extensions: ['txt', 'text', 'log', 'def', 'in', 'list', 'dic'],
    inlineText: true,
  },
  'text/html': { extensions: ['html', 'htm', 'mht', 'mhtml', 'nws'], inlineText: true },
  'text/markdown': { extensions: ['md', 'markdown'], inlineText: true },
  'text/yaml': { extensions: ['yaml', 'yml'], inlineText: true },
  'text/x-rst': { extensions: ['rst'], inlineText: true },
  'application/json': { extensions: ['json'], inlineText: true },
  'application/xml': { extensions: ['xml'], inlineText: true },

  // Code
  'application/javascript': { extensions: ['js', 'mjs', 'jsx'], inlineText: true },
  'application/typescript': { extensions: ['ts', 'tsx'], inlineText: true },
  'text/x-python': { extensions: ['py'], inlineText: true },
  'text/x-java-source': { extensions: ['java'], inlineText: true },
  'text/x-kotlin': { extensions: ['kt', 'kts'], inlineText: true },
  'text/x-scala': { extensions: ['scala'], inlineText: true },
  'text/x-go': { extensions: ['go'], inlineText: true },
  'text/x-rust': { extensions: ['rs'], inlineText: true },
  'text/x-swift': { extensions: ['swift'], inlineText: true },
  'text/x-csharp': { extensions: ['cs'], inlineText: true },
  'text/x-c': { extensions: ['c', 'h'], inlineText: true },
  'text/x-c++': { extensions: ['cpp', 'cc', 'cxx', 'hh', 'hpp'], inlineText: true },
  'text/x-ruby': { extensions: ['rb'], inlineText: true },
  'application/x-php': { extensions: ['php'], inlineText: true },
  'text/x-shellscript': { extensions: ['sh', 'bash', 'zsh', 'ksh', 'bat'], inlineText: true },
  'text/css': { extensions: ['css', 'scss', 'less'], inlineText: true },
  'application/sql': { extensions: ['sql'], inlineText: true },
  'text/x-asm': { extensions: ['asm', 's'], inlineText: true },
  'text/x-perl': { extensions: ['pl'], inlineText: true },
  'text/x-lua': { extensions: ['lua'], inlineText: true },
  'text/x-r': { extensions: ['r'], inlineText: true },
  'text/x-dart': { extensions: ['dart'], inlineText: true },
  'text/x-ini': { extensions: ['conf', 'ini', 'env', 'toml'], inlineText: true },
  'application/x-yaml-config': { extensions: ['dockerfile'], inlineText: true },

  // Subtitles & captions
  'text/x-subrip': { extensions: ['srt'], inlineText: true },
  'text/vtt': { extensions: ['vtt'], inlineText: true },

  // vCard / calendar / email
  'text/vcard': { extensions: ['vcf'], inlineText: true },
  'text/calendar': { extensions: ['ics', 'ifb'], inlineText: true },
  'message/rfc822': { extensions: ['eml', 'mime'], inlineText: true },
} as const;

export type AttachmentMimeType = keyof typeof MIME_EXTENSIONS;

export function isSupportedMimeType(mime: string): mime is AttachmentMimeType {
  return mime in MIME_EXTENSIONS;
}

export function isImageMimeType(mime: string): boolean {
  return mime.startsWith('image/') && isSupportedMimeType(mime);
}

export function isTextMimeType(mime: string): boolean {
  if (isSupportedMimeType(mime)) {
    return MIME_EXTENSIONS[mime].inlineText;
  }
  return mime.startsWith('text/');
}

export function getExtensionForMimeType(mime: string): string | undefined {
  if (isSupportedMimeType(mime)) {
    return MIME_EXTENSIONS[mime].extensions[0];
  }
  return undefined;
}

const SF_FILETYPE_ALIASES: Record<string, string> = {
  word_x: 'docx',
  excel_x: 'xlsx',
  power_point_x: 'pptx',
};

export const SUPPORTED_EXTENSIONS: Set<string> = new Set([
  ...Object.values(MIME_EXTENSIONS).flatMap((v) => v.extensions),
  ...Object.keys(SF_FILETYPE_ALIASES),
]);

export const IMAGE_EXTENSIONS: Set<string> = new Set(
  Object.entries(MIME_EXTENSIONS)
    .filter(([mime]) => mime.startsWith('image/'))
    .flatMap(([, v]) => v.extensions)
);

export function isSupportedAttachmentType(attachmentType: string | undefined): boolean {
  const type = attachmentType?.toLowerCase() ?? '';
  if (isSupportedMimeType(type)) {
    return true;
  }
  const subType = type.includes('/') ? type.split('/').pop()! : type;
  return SUPPORTED_EXTENSIONS.has(subType);
}

export function buildFilePickerAcceptString(): string {
  return Object.values(MIME_EXTENSIONS)
    .flatMap((v) => v.extensions.map((ext: string) => `.${ext}`))
    .join(',');
}
